import React, { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * LessonContentViewer - Premium Rich Content Viewer
 * 
 * Features:
 * - Renders HTML rich content with premium styling
 * - Fallback to Markdown conversion if content_html is empty
 * - Dark mode support
 * - Text zoom controls (A- / A+)
 * - Sanitization with DOMPurify
 * - Responsive design
 * - Custom callouts, tables, blockquotes styling
 */

const LessonContentViewer = ({ 
  lesson, 
  textSize = 100, 
  onTextSizeChange 
}) => {
  // Sanitize and prepare HTML content
  const sanitizedHTML = useMemo(() => {
    if (!lesson) return '';
    
    // Priority: content_html > content (markdown)
    let htmlContent = '';
    
    if (lesson.content_html) {
      htmlContent = lesson.content_html;
    } else if (lesson.content) {
      // Fallback: Convert Markdown to HTML
      try {
        htmlContent = marked.parse(lesson.content, {
          breaks: true,
          gfm: true
        });
      } catch (error) {
        console.error('Error parsing markdown:', error);
        return lesson.content;
      }
    }
    
    // Sanitize HTML with DOMPurify
    const clean = DOMPurify.sanitize(htmlContent, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'hr',
        'strong', 'b', 'em', 'i', 'u', 's', 'mark',
        'ul', 'ol', 'li',
        'blockquote', 'pre', 'code',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'a', 'img',
        'div', 'span',
        'sup', 'sub'
      ],
      ALLOWED_ATTR: [
        'href', 'target', 'rel', 'title',
        'src', 'alt', 'width', 'height',
        'class', 'id', 'style',
        'colspan', 'rowspan',
        'data-callout-type'
      ]
    });
    
    return clean;
  }, [lesson]);
  
  const increaseTextSize = () => {
    const newSize = Math.min(textSize + 10, 150);
    onTextSizeChange?.(newSize);
    toast.success('Taille du texte moderne');
  };
  
  const decreaseTextSize = () => {
    const newSize = Math.max(textSize - 10, 80);
    onTextSizeChange?.(newSize);
    toast.success('Taille du texte réduite');
  };
  
  const resetTextSize = () => {
    onTextSizeChange?.(100);
    toast.success('Taille du texte réinitialisée');
  };
  
  if (!lesson) {
    return (
      <div className="p-6 bg-slate-900/50 rounded-lg border border-slate-700">
        <p className="text-slate-300">Aucun contenu disponible</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Text Size Controls */}
      <div className="flex items-center justify-end gap-2">
        <Button
          onClick={decreaseTextSize}
          size="sm"
          variant="outline"
          className="h-8 w-8 p-0 border-slate-600 hover:bg-slate-700"
          title="Réduire la taille du texte"
        >
          <ZoomOut className="w-4 h-4 text-slate-300" />
        </Button>
        <span className="text-xs text-slate-400 min-w-[3rem] text-center">{textSize}%</span>
        <Button
          onClick={increaseTextSize}
          size="sm"
          variant="outline"
          className="h-8 w-8 p-0 border-slate-600 hover:bg-slate-700"
          title="Augmenter la taille du texte"
        >
          <ZoomIn className="w-4 h-4 text-slate-300" />
        </Button>
        <Button
          onClick={resetTextSize}
          size="sm"
          variant="ghost"
          className="h-8 px-2 text-xs text-slate-400 hover:text-amber-400"
          title="Réinitialiser"
        >
          100%
        </Button>
      </div>
      
      {/* Rich Content Display */}
      <div 
        className="p-3 sm:p-6 md:p-8 rounded-lg" 
        style={{
          maxHeight: '70vh',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <article 
          className="lesson-content"
          style={{
            fontSize: `${textSize}%`,
          }}
          dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
        />
      </div>
    </div>
  );
};

export default LessonContentViewer;
